/**
 * The plan's version at its gate, and what changed in it (spec 2026-11-02,
 * "The plan a person approves is the only contract").
 *
 * Every answer, Send back note, reply on the ticket and accepted Ask change
 * writes a new version of the plan before Approve is offered. The Plan block's
 * fact line says which version is on the page and what made it ("Plan v3 ·
 * updated for your answer"), with the plan's counts after it, and What
 * changed draws that version against the one before it with the diff the Ask
 * cards draw (`SectionDiff`). Version 1 is the plan as Reeve wrote it, so it
 * has nothing to compare.
 *
 * The line is the toggle; the diff mounts where the Plan block puts it, under
 * the block's head (`PlanVersionDiff`), and reads the version only when
 * opened: `GET /api/work-items/:id/plan-versions/:number`, which returns the
 * version and the one before.
 */
import { useEffect, useState } from "react";
import type { PlanVersion, PlanVersionSummary } from "@yagni/shared";
import { Btn } from "../queue/index.js";
import { captureUiError } from "../sentry.js";
import { planSectionDiffs, SectionDiff } from "./AmendmentCards.js";
import {
  PLAN_VERSION_FIRST,
  PLAN_VERSION_HIDE_CHANGES,
  PLAN_VERSION_LOADING,
  PLAN_VERSION_NO_CHANGE,
  PLAN_VERSION_UNREADABLE,
  PLAN_VERSION_WHAT_CHANGED,
  planVersionLine,
  planVersionSides,
} from "./workCopy.js";

export function PlanVersionLine({
  version,
  counts,
  open,
  onToggle,
}: {
  version: Pick<PlanVersionSummary, "id" | "number" | "cause">;
  /** The plan's counts, said after the version: "5 steps · 3 files". */
  counts?: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <span className="pvline" data-testid="plan-version-line">
      <span data-testid="plan-version">{planVersionLine(version.number, version.cause.kind)}</span>
      {counts ? <span> · {counts}</span> : null}
      <Btn
        variant="quiet"
        size="sm"
        aria-expanded={open}
        data-testid="plan-version-toggle"
        onClick={onToggle}
      >
        {open ? PLAN_VERSION_HIDE_CHANGES : PLAN_VERSION_WHAT_CHANGED}
      </Btn>
    </span>
  );
}

type VersionRead =
  | { state: "loading" }
  | { state: "failed" }
  | { state: "read"; version: PlanVersion; previous: PlanVersion | null };

/** This version against the one before, section by section. */
export function PlanVersionDiff({
  workItemId,
  version,
}: {
  workItemId: string;
  version: Pick<PlanVersionSummary, "id" | "number">;
}) {
  const [read, setRead] = useState<VersionRead>({ state: "loading" });
  const [attempt, setAttempt] = useState(0);
  const first = version.number <= 1;
  useEffect(() => {
    if (first) return;
    let cancelled = false;
    setRead({ state: "loading" });
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/plan-versions/${version.number}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Plan version read failed (${res.status})`);
        const body = (await res.json()) as { version?: PlanVersion; previous?: PlanVersion | null };
        if (!body.version) throw new Error("Plan version read returned no version");
        if (!cancelled)
          setRead({ state: "read", version: body.version, previous: body.previous ?? null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureUiError(err, { area: "Work", operation: "read_plan_version", workItemId });
        setRead({ state: "failed" });
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, version.number, version.id, first, attempt]);

  if (first) {
    return (
      <p className="amd-note" data-testid="plan-version-diff">
        {PLAN_VERSION_FIRST}
      </p>
    );
  }
  if (read.state === "loading") {
    return (
      <p className="amd-note" data-testid="plan-version-diff" aria-busy="true">
        {PLAN_VERSION_LOADING}
      </p>
    );
  }
  if (read.state === "failed") {
    return (
      <p className="amd-note is-warn" data-testid="plan-version-diff" role="alert">
        {PLAN_VERSION_UNREADABLE}{" "}
        <Btn variant="quiet" size="sm" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </Btn>
      </p>
    );
  }
  const before = read.previous?.pageMarkdown ?? "";
  const after = read.version.pageMarkdown;
  if (!read.previous) {
    return (
      <p className="amd-note" data-testid="plan-version-diff">
        {PLAN_VERSION_FIRST}
      </p>
    );
  }
  const changed = planSectionDiffs(before, after).length > 0;
  return (
    <div className="pvdiff" data-testid="plan-version-diff">
      {changed ? (
        <SectionDiff before={before} after={after} labels={planVersionSides(version.number)} />
      ) : (
        <p className="amd-note">{PLAN_VERSION_NO_CHANGE}</p>
      )}
    </div>
  );
}
